import { Component, OnDestroy, ViewChild } from '@angular/core';
import { TableComponent } from '../../shared/table/table.component';
import { TableSearchComponent } from '../../shared/table-search/table-search.component';
import * as TestPanel from '../../shared/constants/sampletestconstants';
import { MatDialog } from '@angular/material/dialog';
import { SampleData, SampleTestInterface, SampleTestResponse } from '../../interfaces/sample-test-panel';
import { MatTableDataSource } from '@angular/material/table';
import { RowData } from '../../shared/interfaces/table.data';
import { SampletestService } from '../../services/sampletest/sampletest.service';
import { SampleInfoPanelComponent } from './info-panel/sample-info-panel.component';
import { ModalDialogComponent } from '../../shared/modal-dialog/modal-dialog.component';
import { SampleTestRunModalComponent } from './sample-test-run-modal/sample-test-run-modal.component';
import { MessageModalService } from '../../services/message-modal/message-modal.service';
import { UtcDatePipe } from '../../shared/pipes/utc-date.pipe';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { SampleEditModalComponent } from './edit-modal/sample-edit-modal.component';
import { AnalysisDialogComponent } from '../sample-pages/sample-analysis-dialog/analysis-dialog/analysis-dialog.component';
import { ButtonsComponent } from '../../shared/buttons/buttons.component';
import { SharePopupComponent } from '../../shared/share-popup/share-popup.component';

@Component({
  selector: 'app-sample-testpanel',
  standalone: true,
  imports: [TableComponent, TableSearchComponent, ButtonsComponent, SampleInfoPanelComponent],
  providers: [UtcDatePipe],
  templateUrl: './sample-testpanel.component.html',
  styleUrl: './sample-testpanel.component.css',
})
export class SampleTestpanelComponent implements OnDestroy {
  constructor(
    private dialog: MatDialog,
    private sampleService: SampletestService,
    private messageModal: MessageModalService,
    private utcDatePipe: UtcDatePipe
  ) {}

  @ViewChild(TableComponent)
  tableComponent!: TableComponent<SampleTestInterface>;

  dataSource: MatTableDataSource<RowData<SampleTestInterface>> = new MatTableDataSource<RowData<SampleTestInterface>>(
    []
  );
  displayedColumnLabels: string[] = TestPanel.displayedColumnLabels;
  displayedColumns: string[] = TestPanel.displayedColumns;
  btnName: string = $localize`Share`;
  btnMatStroked = 'mat-stroked-button';
  btnStyleStroked = 'btn-stroked';
  showCheckboxInTable: boolean = true;
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';
  btnStyleFilledSmallForPrimaryBtnDisabled = 'btn-filled-small-disabled';
  btnMatFlat: string = 'mat-flat-button';
  showActionBtnsInPagination: boolean = true;
  private destroy$: Subject<void> = new Subject<void>();

  selectedSampleItems: SampleTestInterface[] = [];

  isInfoPanelOpen = false;
  selectedSampleData: SampleData | null = null;
  selectedSampleName: string | null = null;

  /** Row with an open menu/panel/dialog; drives the "active" row highlight. */
  activeRowSampleId: number | null = null;

  readonly isRowActiveFn = (row: RowData<SampleTestInterface>): boolean =>
    this.activeRowSampleId != null && row.data.sampleId === this.activeRowSampleId;

  ngOnInit() {
    this.loadData();
    if (this.dataSource) {
      this.dataSource.filterPredicate = this.createFilterPredicate();
    }
  }

  loadData() {
    this.sampleService
      .getAllSamples()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.success) {
            const samplesData: any[] = response.data;

            const rowData: RowData<SampleTestInterface>[] = samplesData.map((sample) => {
              const groupNames: string[] = sample.Groups
                ? (sample.Groups as { GroupName: string; IsPrimary: boolean }[])
                    .sort((a, b) => (a.IsPrimary === b.IsPrimary ? 0 : a.IsPrimary ? -1 : 1))
                    .map((g) => g.GroupName)
                : [];
              const analysisNames: string[] = sample.AnalysisNames;
              // 3. Create the data object for the table row.
              const tableRowData: SampleTestInterface = {
                user: sample.UserName,
                sampleName: sample.SampleName,
                sampleDescription: sample.SampleDescription,
                sampleId: sample.SampleID,
                createdOn: sample.CreatedOn,
                groupName: { list: groupNames },
                analysisName: { list: analysisNames },
                fullGroups: sample.Groups,
              } as any;

              return {
                data: tableRowData,
                selected: false,
              };
            });

            this.dataSource = new MatTableDataSource<RowData<SampleTestInterface>>(rowData);
            this.dataSource.filterPredicate = this.createFilterPredicate();
          } else {
            this.messageModal.openModal($localize`Error`, $localize`${response.message}`);
          }
        },
        error: (err) => {
          this.messageModal.openModal($localize`Error`, $localize`Error while fetching list of samples.`);
        },
      });
  }

  public createFilterPredicate(): (item: RowData<SampleTestInterface>, filter: string) => boolean {
    return (item: RowData<SampleTestInterface>, filter: string) => {
      const terms = filter.trim().toLowerCase().split(/\s+/); // split by space

      const dataValues = Object.entries(item.data)
        .filter(([, val]) => val != null)
        .map(([key, val]) => {
          if (typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))) {
            // Assume it's an ISO date string -> extract only the date part
            return val.split('T')[0].toLowerCase();
          }
          if ((key === 'analysisName' || key === 'groupName') && val && (val as any).list) {
            return (val as any).list.join(' ').toLowerCase();
          }
          return val.toString().toLowerCase();
        });

      // Each term must be found in at least one field
      return terms.every((term) => dataValues.some((value) => value.includes(term)));
    };
  }

  onFilterChanged(filterValue: string): void {
    this.dataSource.filter = filterValue;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onRowMenuOpened(row: SampleTestInterface): void {
    this.activeRowSampleId = row.sampleId;
  }

  onRowMenuClosed(): void {
    // Keep the highlight if a menu item opened the info panel or a dialog — that surface now owns it.
    if (!this.isInfoPanelOpen && this.dialog.openDialogs.length === 0) {
      this.activeRowSampleId = null;
    }
  }

  onInfoPanelClosed(): void {
    this.isInfoPanelOpen = false;
    this.activeRowSampleId = null;
  }

  handleRunClick(event: SampleTestInterface) {
    this.selectedSampleItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.openCreateModal(event.sampleId);
  }

  openCreateModal(sampleId: number) {
    this.activeRowSampleId = sampleId;
    const dialogRef = this.dialog.open(SampleTestRunModalComponent, {
      width: '50rem',
      data: {
        title: $localize`Choose Test Panel`,
        sampleId: sampleId,
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.activeRowSampleId = null;
      this.loadData();
    });
  }

  handleGetAnalysisClick(event: any) {
    this.selectedSampleItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.activeRowSampleId = event?.sampleId ?? null;
    this.sampleService
      .getAllAnalysisForSample(event?.sampleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const dialogRef = this.dialog.open(AnalysisDialogComponent, {
            width: `50rem`,
            height: '38rem',
            panelClass: 'analysis-dialog',
            position: { right: '10px' },
            data: {
              response: response.data,
              sampleName: event?.sampleName,
            },
          });
          dialogRef.afterClosed().subscribe(() => {
            this.activeRowSampleId = null;
            this.loadData();
          });
        },
        error: (err) => {
          this.activeRowSampleId = null;
          this.messageModal.openModal(
            $localize`Error`,
            $localize`Error while fetching all the analysis for specific sampleId.`
          );
        },
      });
  }

  handleGetInfoClick(event: SampleTestInterface) {
    this.activeRowSampleId = event.sampleId;
    this.isInfoPanelOpen = true;
    this.sampleService
      .getSampleInfo(event.sampleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (sampleData) => {
          this.selectedSampleData = sampleData;
        },
        error: (err) => {
          this.activeRowSampleId = null;
          this.isInfoPanelOpen = false;
          this.messageModal.openModal($localize`Error`, $localize`Error while fetching the Sample Info.`);
        },
      });
  }

  async handleSampleEditClick(event: SampleTestInterface) {
    this.selectedSampleItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.activeRowSampleId = event.sampleId;
    const dialogRef = this.dialog.open(SampleEditModalComponent, {
      width: `60rem`,
      data: {
        sampleData: event,
      },
    });
    const res = await lastValueFrom(dialogRef.afterClosed());
    this.activeRowSampleId = null;
    if (res && res.event === 'SUBMIT') {
      const editedData = res.data;
      this.sampleService
        .editSample(res.sampleId, editedData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.messageModal.openModal($localize`Success`, $localize`Sample updated successfully.`);
            this.loadData();
          },
          error: (err) => {
            console.error(err);
            this.messageModal.openModal($localize`Error`, $localize`Error while editing the Sample Result.`);
          },
        });
    } else if (res.event === 'RELOAD') {
      this.messageModal.openModal($localize`Success`, $localize`Sample updated successfully.`);
      this.loadData();
    }
  }

  openDeleteSampleModal(event: SampleTestInterface) {
    this.selectedSampleItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.activeRowSampleId = event.sampleId;
    const sampleName = event.sampleName;
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '600px',
      data: {
        title: $localize`Delete Sample?`,
        description: $localize`Once deleted, <b>${sampleName}</b> will no longer be available in the Samples list.`,
        primaryButtonLabel: $localize`Yes, Delete`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true, // Show primary button
        showSecondaryButton: true, // Secondary button is always shown
      },
    });
    dialogRef.afterClosed().subscribe(() => {
      this.activeRowSampleId = null;
    });

    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      this.handleDeleteSample(modalInstance, event);
    });
  }

  handleDeleteSample(modalInstance: ModalDialogComponent, event: SampleTestInterface) {
    this.deleteSample(event).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadData();
          modalInstance.closeModal(true);
        } else {
          modalInstance.showError($localize`${response.message}`);
        }
      },
      error: (err) => {
        modalInstance.showError($localize`Error while deleting the Sample.`);
      },
    });
  }

  deleteSample(event: SampleTestInterface) {
    return this.sampleService.deleteSample(event.sampleId, event.analysisID ?? undefined);
  }

  convertISO8601ToHuman(isoDuration: string | null | undefined): string {
    if (!isoDuration) {
      return '--'; // Return a default display value
    }

    const regex = /P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?/;
    const matches = isoDuration.match(regex);

    if (!matches) {
      throw new Error('Invalid ISO 8601 duration format');
    }

    const days = parseInt(matches[1] || '0', 10);
    const hours = parseInt(matches[2] || '0', 10);
    const minutes = parseInt(matches[3] || '0', 10);
    const seconds = parseInt(matches[4] || '0', 10);

    const parts = [];
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
    if (seconds > 0) parts.push(`${seconds} second${seconds > 1 ? 's' : ''}`);
    return parts.length > 0 ? parts.join(', ') : '0 seconds';
  }

  handleCheckboxChange(rowData: { data: SampleTestInterface; selected: boolean }) {
    if (rowData.selected) {
      const exists = this.selectedSampleItems.some((item) => item.sampleId === rowData.data.sampleId);
      if (!exists) {
        this.selectedSampleItems.push(rowData.data);
      }
    } else {
      this.selectedSampleItems = this.selectedSampleItems.filter((item) => item.sampleId !== rowData.data.sampleId);
    }
  }

  onShareClicked() {
    const selectedIds = this.selectedSampleItems.map((item) => item.sampleId);

    const dialogRef = this.dialog.open(SharePopupComponent, {
      width: '550px',
      data: {
        Ids: selectedIds,
        title: 'Sample',
        resource_path: 'samples',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedSampleItems = [];
        if (this.tableComponent) {
          this.tableComponent.resetSelections();
        }
        this.loadData();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
